// BranchDropTab.tsx
import React, {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {useFormikContext} from 'formik'
import {loadBranches} from '../branch/Api'
import {IBranchModel} from '../branch/BranchModel'
import {showErrorToast} from '../../../../shared/components/ToastUtils'
import {
  IBranchDropSetting,
  ILocationModel,
  KARSILAMA_TURU_OPTIONS,
  MESAI_SAATLERI_OPTIONS,
} from './LocationModel'

type Props = {
  onOpenDropDistances: (branch: IBranchDropSetting) => void
}

const buildDefaultSetting = (branch: IBranchModel): IBranchDropSetting => ({
  branchId: String(branch.id ?? ''),
  branchName: branch.branchName || '',
  isExitBranch: false,
  greetingType: '',
  workingHoursType: '',
  specialMail: '',
  specialPhone: '',
})

export const BranchDropTab: FC<Props> = ({onOpenDropDistances}) => {
  const {values, setFieldValue} = useFormikContext<ILocationModel>()
  const [branches, setBranches] = useState<IBranchModel[]>([])
  const [loading, setLoading] = useState(false)

  const settings = useMemo<IBranchDropSetting[]>(
    () => values.branchDropSettings || [],
    [values.branchDropSettings]
  )

  const fetchBranches = useCallback(async () => {
    setLoading(true)
    try {
      const res = await loadBranches(0, 100, 'branchName', 'asc')
      const data = res.data.payload || (res.data as any)
      const list: IBranchModel[] = data?.content || data || []
      setBranches(list)
    } catch (err) {
      const error = err as any
      if (error?.response?.status === 401) {
        window.location.href = '/login'
        return
      }
      console.error(error)
      showErrorToast('Şubeler yüklenirken bir hata oluştu')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBranches()
  }, [fetchBranches])

  const getSetting = useCallback(
    (branch: IBranchModel): IBranchDropSetting => {
      const id = String(branch.id ?? '')
      return settings.find((s) => s.branchId === id) || buildDefaultSetting(branch)
    },
    [settings]
  )

  const updateSetting = useCallback(
    (branch: IBranchModel, patch: Partial<IBranchDropSetting>) => {
      const id = String(branch.id ?? '')
      const existing = settings.find((s) => s.branchId === id)
      const next = existing
        ? settings.map((s) => (s.branchId === id ? {...s, ...patch} : s))
        : [...settings, {...buildDefaultSetting(branch), ...patch}]
      setFieldValue('branchDropSettings', next)
    },
    [settings, setFieldValue]
  )

  const sortedBranches = useMemo(
    () => [...branches].sort((a, b) => (a.branchName || '').localeCompare(b.branchName || '', 'tr')),
    [branches]
  )

  if (loading) {
    return (
      <div className='card mb-5'>
        <div className='card-body d-flex justify-content-center p-10'>
          <div className='spinner-border' role='status'>
            <span className='visually-hidden'>Yükleniyor...</span>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='card mb-5'>
      <div className='card-body p-0'>
        <div className='table-responsive'>
          <table className='table table-row-bordered table-row-gray-200 align-middle gy-4 gs-5 mb-0'>
            <thead className='bg-light'>
              <tr className='fw-bold fs-7 text-gray-800 text-uppercase'>
                <th style={{width: 160}}>Çıkış Şubesi Seçimi</th>
                <th style={{width: 180}}>Karşılama Türü</th>
                <th style={{width: 160}}>Mesai Saatleri</th>
                <th style={{minWidth: 180}}>Özel Mail</th>
                <th style={{minWidth: 160}}>Özel Tel</th>
                <th style={{minWidth: 220}}>Şube Adı</th>
                <th style={{width: 160}}>Drop Ayarları</th>
              </tr>
            </thead>
            <tbody>
              {sortedBranches.length === 0 ? (
                <tr>
                  <td colSpan={7} className='text-center text-muted py-10'>
                    <i className='bi bi-inbox fs-1 d-block mb-2 text-muted'></i>
                    Şube bulunamadı
                  </td>
                </tr>
              ) : (
                sortedBranches.map((branch) => {
                  const setting = getSetting(branch)
                  return (
                    <tr key={branch.id}>
                      <td>
                        <div className='form-check form-check-custom form-check-solid'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={setting.isExitBranch}
                            onChange={(e) =>
                              updateSetting(branch, {isExitBranch: e.target.checked})
                            }
                          />
                        </div>
                      </td>
                      <td>
                        <select
                          className='form-select form-select-sm'
                          value={setting.greetingType}
                          onChange={(e) =>
                            updateSetting(branch, {greetingType: e.target.value})
                          }
                        >
                          <option value=''>Seçiniz</option>
                          {KARSILAMA_TURU_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          className='form-select form-select-sm'
                          value={setting.workingHoursType || 'LOKASYON'}
                          onChange={(e) =>
                            updateSetting(branch, {workingHoursType: e.target.value})
                          }
                        >
                          {MESAI_SAATLERI_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type='email'
                          className='form-control form-control-sm'
                          value={setting.specialMail}
                          onChange={(e) =>
                            updateSetting(branch, {specialMail: e.target.value})
                          }
                        />
                      </td>
                      <td>
                        <input
                          type='text'
                          className='form-control form-control-sm'
                          value={setting.specialPhone}
                          onChange={(e) =>
                            updateSetting(branch, {specialPhone: e.target.value})
                          }
                        />
                      </td>
                      <td>
                        <button
                          type='button'
                          className='btn btn-link btn-sm fw-semibold p-0 text-primary text-uppercase'
                          onClick={() => onOpenDropDistances(setting)}
                          title={branch.branchName}
                        >
                          {branch.branchName}
                        </button>
                      </td>
                      <td>
                        <button
                          type='button'
                          className='btn btn-link btn-sm fw-semibold p-0 text-primary'
                          onClick={() => onOpenDropDistances(setting)}
                        >
                          Drop Mesafeleri
                        </button>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
